import type { VerifyResponse } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../client/core/getCore';
import type { DynamicCoreState } from '../../../client/core/types';
import type { DynamicClient } from '../../../client/types';
import { setCookie } from '../../../utils/setCookie';
import { isCookieEnabled } from '../../projectSettings/isCookieEnabled';
import { restoreUserSharesForAllWalletAccounts } from '../../waas/restoreUserSharesForAllWalletAccounts';
import { DYNAMIC_AUTH_COOKIE_NAME } from '../consts';
import { checkAndRaiseWalletAccountsChangedEvent } from './checkAndRaiseWalletAccountsChangedEvent';
import { parseElevatedAccessToken } from './elevatedAccessTokens/parseElevatedAccessToken';
import { upsertElevatedAccessToken } from './elevatedAccessTokens/upsertElevatedAccessToken';

type UpdateAuthFromVerifyResponseParams = {
  response: VerifyResponse;
};

export const updateAuthFromVerifyResponse = (
  { response }: UpdateAuthFromVerifyResponseParams,
  client: DynamicClient
) => {
  const core = getCore(client);

  const previousState = { ...core.state.get() };

  const { user, minifiedJwt, jwt, expiresAt, mfaToken, elevatedAccessToken } =
    response;

  // Expires at is in seconds, so we need to convert it to milliseconds
  const sessionExpiresAt = new Date(expiresAt * 1000);

  const newState: Partial<DynamicCoreState> = {
    legacyToken: jwt ?? null,
    sessionExpiresAt: sessionExpiresAt,
    token: minifiedJwt ?? null,
    user,
  };

  // only override the mfaToken if a new one is provided in the response
  if (mfaToken) {
    newState.mfaToken = mfaToken;
  }

  // Add elevated access token to the array if a new one is provided in the response
  // Policy: Only one token per normalized scope-set is allowed (old tokens are replaced)
  // Always preserve existing tokens to ensure they're synced to storage
  const currentTokens = core.state.get().elevatedAccessTokens || [];
  newState.elevatedAccessTokens = currentTokens;
  
  if (elevatedAccessToken) {
    const parsedToken = parseElevatedAccessToken(elevatedAccessToken);
    if (parsedToken) {
      newState.elevatedAccessTokens = upsertElevatedAccessToken({
        currentTokens,
        newToken: parsedToken,
      });
    }
  }

  core.state.set(newState);

  /**
   * For customers using a sandbox environment with cookies enabled, we need to set the cookie
   * programmatically because Redcoast won't set the cookie via headers. We set the cookie programmatically
   * so customers can access the cookie from document.cookie consistently between sandbox and live environments.
   */
  if (minifiedJwt && isCookieEnabled(client)) {
    const expireDate = sessionExpiresAt.toUTCString();

    setCookie(
      `${DYNAMIC_AUTH_COOKIE_NAME}=${minifiedJwt}; expires=${expireDate}; path=/; SameSite=Lax`
    );
  }

  checkAndRaiseWalletAccountsChangedEvent({ previousState }, client);

  // if the user is logging in, we should restore user shares
  // for all wallet accounts for performance reasons
  const isUserLoggingIn = !previousState.user && Boolean(newState.user);
  if (isUserLoggingIn) {
    void restoreUserSharesForAllWalletAccounts(client);
  }
};
